import { StateCreator, create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { SellingPlanApiResponse } from "@smartrr/shared/entities/SellingPlanGroup";
import { SmartrrApi } from "@customer-app/api";
import { StatusCodes } from "http-status-codes";

interface SellingPlanGroupStore {
  sellingPlans: Record<string, SellingPlanApiResponse>;
  isLoading: boolean;

  actions: {
    fetchSellingPlans(): Promise<void>;
  };

  internal: {
    whileLoading<Type>(fn: () => Promise<Type>): Promise<Type>;
  };
}

const sellingPlanGroupStoreSlice: StateCreator<SellingPlanGroupStore, [["zustand/immer", never]]> = (
  set,
  get
) => ({
  sellingPlans: {},
  isLoading: false,

  actions: {
    async fetchSellingPlans(): Promise<void> {
      if (get().isLoading) {
        return;
      }

      await get().internal.whileLoading(async () => {
        const response = await SmartrrApi.sellingPlanGroups.getSellingPlanGroups();

        if (response.status === StatusCodes.OK) {
          const sellingPlanGroups = response.body;
          for (const sellingPlanGroup of sellingPlanGroups) {
            for (const sellingPlan of sellingPlanGroup.sellingPlans) {
              get().sellingPlans[sellingPlan.shopifyId!] = sellingPlan;
            }
          }
        }
      });
    },
  },

  internal: {
    async whileLoading<Type>(fn: () => Promise<Type>): Promise<Type> {
      set({
        isLoading: true,
      });
      const response = await fn();
      set({
        isLoading: false,
      });
      return response;
    },
  },
});

const useSellingPlanGroupStore = create<SellingPlanGroupStore>()(immer(sellingPlanGroupStoreSlice));
export const SellingPlanGroupStoreAccess = {
  useActions(): SellingPlanGroupStore["actions"] {
    return useSellingPlanGroupStore(state => state.actions);
  },
  useSellingPlan(id?: string): SellingPlanApiResponse | null {
    return (
      useSellingPlanGroupStore(state => {
        return id ? state.sellingPlans[id] : null;
      }) ?? null
    );
  },
};

export const SellingPlanGroupStoreTest = {
  initialState: useSellingPlanGroupStore.getState(),
  initialize(sellingPlans: Record<string, SellingPlanApiResponse>) {
    this.reset();
    this.get().sellingPlans = sellingPlans;
  },
  reset() {
    useSellingPlanGroupStore.setState(this.initialState);
  },
  get() {
    return useSellingPlanGroupStore.getState();
  },
  actions() {
    return useSellingPlanGroupStore.getState().actions;
  },
};
